<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IAffiliateTrackerPayload } from '@modules/affiliate-trackers/types';
import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATE_TRACKERS } from '@modules/affiliate-trackers/constants';
import { useAffiliateTrackersCreateEditMutation } from '@modules/affiliate-trackers/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useUserSupervision } from '@modules/user/composables';

import type { AffiliateTrackersEditButtonProps } from './types';

const props = withDefaults(defineProps<AffiliateTrackersEditButtonProps>(), {});

const { getTranslation } = useI18n();
const { isSupervision } = useUserSupervision();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useAffiliateTrackersCreateEditMutation();

const formSchema = computed<FormSchema<IAffiliateTrackerPayload>>(() => ({
  description: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_EDIT_DESCRIPTION').value,
    required: true,
    value: props.item.description,
    readonly: isSupervision.value,
  },
}));

const form = ref<VFormRef>();
const isVisibleForm = ref(false);

function showModal(): void {
  isVisibleForm.value = true;
}

function onModalHide(): void {
  isVisibleForm.value = false;
}

function onSubmit(): void {
  form.value?.submit();
}

async function onFormSave(values: IAffiliateTrackerPayload): Promise<void> {
  await mutateAsync({
    ...props.item,
    ...values,
  });
  onModalHide();
}
</script>

<template>
  <div v-data-qa-auto-id="'AffiliateTrackersEditButton'">
    <VIconButton
      :loading="isLoading"
      icon="pi-file-edit"
      @click="showModal"
    />
    <VDialog
      :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_EDIT_TITLE')"
      :visible="isVisibleForm"
      @hide="onModalHide"
    >
      <VForm
        ref="form"
        :schema="formSchema"
        :is-loading="isLoading"
        :custom-errors="errors"
        @interacted="clearError"
        @submit="onFormSave"
      />
      <template #footer>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_EDIT_CANCEL')"
          outlined
          @click="onModalHide"
        />
        <VButton
          :disabled="isSupervision"
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_TRACKERS, 'MODAL_EDIT_SAVE')"
          @click="onSubmit"
        />
      </template>
    </VDialog>
  </div>
</template>
